import { ObjectId } from "bson";
import { SchemaType } from "./advanced-flow/schemas/schemas";
import { Category } from "./advanced-flow/triggers";
import {
  Alignment,
  ButtonLinkType,
  CartLayoutType,
  EmailBlockType,
  EmailHeaderType,
  FontFamily,
  FontWeight,
  IEmailTemplate,
  MenuItem,
  ProductSelectionType,
  Section,
  Size,
  SocialPlatform,
  TemplateType,
} from "./email-builder";
import { UpsellProductSource } from "./team";

export const headerDefaultLogo: Section.Header = {
  type: EmailBlockType.HEADER,
  headerType: EmailHeaderType.LOGO,
  layout: Alignment.CENTER,
  sectionColor: "#FFFFFF",
  padding: 30,
  sectionPadding: { top: 30, right: 30, bottom: 30, left: 30 },
  imageUrl: "",
  logoHeight: 55,
  imageHeight: 55,
  text: "",
  fontSize: 16,
  fontFamily: FontFamily.ARIAL,
  textColor: "#000000",
};

export const headerDefaultImage: Section.Header = {
  type: EmailBlockType.HEADER,
  headerType: EmailHeaderType.IMAGE,
  layout: Alignment.CENTER,
  sectionColor: "#FFFFFF",
  padding: 30,
  sectionPadding: { top: 30, right: 30, bottom: 30, left: 30 },
  imageUrl: "",
  logoHeight: 55,
  imageHeight: 55,
  text: "",
  fontSize: 16,
  fontFamily: FontFamily.ARIAL,
  textColor: "#000000",
};

export const headerDefaultText: Section.Header = {
  type: EmailBlockType.HEADER,
  headerType: EmailHeaderType.TEXT,
  layout: Alignment.CENTER,
  sectionColor: "#FFFFFF",
  padding: 30,
  sectionPadding: { top: 30, right: 30, bottom: 30, left: 30 },
  text: "",
  imageUrl: "",
  logoHeight: 55,
  imageHeight: 55,
  fontSize: 16,
  fontFamily: FontFamily.ARIAL,
  textColor: "#000000",
  clickthroughUrl: undefined,
  altText: undefined,
};

export const footerDefault: Section.Footer = {
  type: EmailBlockType.FOOTER,
  padding: { top: 40, right: 0, bottom: 40, left: 0 },
  sectionPadding: { top: 40, right: 0, bottom: 40, left: 0 },
  horizontalPadding: Size.MEDIUM,
  verticalPadding: Size.MEDIUM,
  sectionColor: "#ffffff",
  textColor: "#000000",
  alignment: Alignment.CENTER,
  schemaFieldName: "unsubscribeLink",
  fontFamily: FontFamily.ARIAL,
};

export const textDefault: Section.Text = {
  type: EmailBlockType.TEXT,
  textColor: "#000000",
  padding: 12,
  sectionPadding: { top: 12, right: 12, bottom: 12, left: 12 },
  sectionColor: "#ffffff",
  text: "",
  linkColor: "#1E90FF",
  fontSize: 12,
  fontFamily: FontFamily.ARIAL,
};

export const buttonDefault: Section.Button = {
  type: EmailBlockType.BUTTON,
  width: 100,
  height: 40,
  size: Size.MEDIUM,
  alignment: Alignment.CENTER,
  cornerRadius: 8,
  buttonText: "Button",
  horizontalPadding: Size.SMALL,
  verticalPadding: Size.SMALL,
  padding: { top: 0, right: 0, bottom: 0, left: 0 },
  sectionPadding: { top: 0, right: 0, bottom: 0, left: 0 },
  fillColor: "#000000",
  strokeColor: "#000000",
  textColor: "#FFFFFF",
  strokeWeight: 0,
  fontFamily: FontFamily.ARIAL,
  fontSize: 16,
  sectionColor: "#FFFFFF",
  linkType: ButtonLinkType.WEB_PAGE,
};

export const reviewRequestDefault: Section.ReviewRequest = {
  type: EmailBlockType.REVIEW_REQUEST,
  padding: 40,
  sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
  sectionColor: "#ffffff",
  sectionHeader: "Review Request",
  imageCornerRadius: 8,
  imageSize: Size.MEDIUM,
  buttonHeight: 40,
  buttonWidth: 200,
  buttonColor: "#000000",
  buttonTextColor: "#ffffff",
  buttonSize: Size.MEDIUM,
  buttonCornerRadius: 8,
  textColor: "#000000",
  fontFamily: FontFamily.ARIAL,
  schemaFieldName: "reviewRequest",
};

export const columnDefault: Section.Column = {
  type: EmailBlockType.COLUMN,
  columns: [],
  columnCount: 2,
  sectionColor: "#ffffff",
  sectionPadding: { top: 8, right: 8, bottom: 8, left: 8 },
};

export const lineDefault: Section.Line = {
  type: EmailBlockType.LINE,
  sectionColor: "#ffffff",
  color: "#333333",
  padding: { top: 8, right: 8, bottom: 8, left: 8 },
  sectionPadding: { top: 8, right: 8, bottom: 8, left: 8 },
  horizontalPadding: Size.MEDIUM,
  verticalPadding: Size.MEDIUM,
};

export const spacerDefault: Section.Spacer = {
  type: EmailBlockType.SPACER,
  height: 50,
  sectionColor: "#FFFFFF",
  sectionPadding: { top: 50, right: 0, bottom: 0, left: 0 },
};

export const imageDefault: Section.Image = {
  type: EmailBlockType.IMAGE,
  imageUrl: "",
  padding: { top: 0, right: 0, bottom: 0, left: 0 },
  sectionPadding: { top: 0, right: 0, bottom: 0, left: 0 },
  horizontalPadding: Size.MEDIUM,
  verticalPadding: Size.MEDIUM,
  sectionColor: "#ffffff",
  showCaption: false,
  caption: "",
  clickthroughUrl: undefined,
  altText: "",
  aspectRatio: 1,
};

export const trackingInfoDefault: Section.TrackingInfo = {
  type: EmailBlockType.TRACKING_INFO,
  sectionColor: "#ffffff",
  padding: 40,
  sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
  textColor: "#000000",
  buttonVisible: true,
  buttonHeight: 40,
  buttonWidth: 200,
  buttonCornerRadius: 8,
  statusIndicatorColor: "#000000",
  statusIndicatorInnerColor: "#000000",
  fontFamily: FontFamily.ARIAL,
  buttonColor: "#000000",
  buttonTextColor: "#ffffff",
  buttonSize: Size.MEDIUM,
  schemaFieldName: "tracker",
};

export const trackableSummaryDefault: Section.TrackableSummary = {
  type: EmailBlockType.TRACKABLE_SUMMARY,
  showOrderInformation: false,
  showCostSummary: false,
  showPaymentInformation: false,
  showCustomerInformation: false,
  sectionColor: "#ffffff",
  padding: 40,
  sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
  textColor: "#000000",
  fontFamily: FontFamily.ARIAL,
  imageSize: Size.MEDIUM,
  imageCornerRadius: 8,
  schemaFieldName: "orderSummary", // same field name for returns
};

export const interactiveCartDefault: Section.InteractiveCart = {
  type: EmailBlockType.INTERACTIVE_CART,
  source: UpsellProductSource.RECOMMENDATIONS,
  padding: 40,
  sectionPadding: { top: 40, right: 40, bottom: 40, left: 40 },
  sectionColor: "#ffffff",
  textColor: "#000000",
  fontFamily: FontFamily.ARIAL,
  checkoutButton: { ...buttonDefault, buttonText: "Checkout", as: "button" },
  lineItemButtons: {
    ...buttonDefault,
    fillColor: "#ffffff",
    textColor: "#000000",
    strokeColor: "#d6d6d6",
    strokeWeight: 1,
    as: "button",
  },
  showPrice: true,
  imageCornerRadius: 8,
  numberOfProducts: 3,
  imageSize: Size.MEDIUM,
  schemaFieldName: "interactiveCart",
  columns: 1,
  manuallySelectedProducts: [],
  productSelectionType: ProductSelectionType.DYNAMIC,
  layoutType: CartLayoutType.ROWS,
  alignment: Alignment.LEFT,
  stackOnMobile: true,
};

export const defaultMenuItem: MenuItem = {
  id: crypto.randomUUID(),
  label: "Item",
};

export const menuDefault: Section.Menu = {
  type: EmailBlockType.MENU,
  menuItems: [defaultMenuItem, defaultMenuItem, defaultMenuItem],
  alignment: Alignment.CENTER,
  fontFamily: FontFamily.ARIAL,
  fontSize: 12,
  textColor: "#000000",
  sectionPadding: { top: 8, right: 8, bottom: 8, left: 8 },
  linkColor: "#000000",
  sectionColor: "#ffffff",
};

export const socialsDefault: Section.Socials = {
  type: EmailBlockType.SOCIALS,
  socialLinks: [
    {
      id: crypto.randomUUID(),
      platform: SocialPlatform.FACEBOOK,
      url: "https://facebook.com",
    },
    {
      id: crypto.randomUUID(),
      platform: SocialPlatform.INSTAGRAM,
      url: "https://instagram.com",
    },
    {
      id: crypto.randomUUID(),
      platform: SocialPlatform.TWITTER,
      url: "https://twitter.com",
    },
    {
      id: crypto.randomUUID(),
      platform: SocialPlatform.TIKTOK,
      url: "https://tiktok.com",
    },
  ],
  alignment: Alignment.CENTER,
  iconColor: "#A3A3A3",
  iconPadding: 8,
  sectionPadding: { top: 20, right: 100, bottom: 20, left: 100 },
  sectionColor: "#ffffff",
};

export const discountDefault: Section.Discount = {
  type: EmailBlockType.DISCOUNT,
  discountId: undefined,
  fontFamily: FontFamily.ARIAL,
  fontSize: 32,
  textColor: "#000000",
  sectionPadding: { top: 8, right: 120, bottom: 8, left: 120 },
  sectionColor: "#ffffff",
  alignment: Alignment.CENTER,
  blockBackgroundColor: "#E5E5E5",
  fontWeight: FontWeight.NORMAL,
};

export function getDefaultSections(
  schemaType: SchemaType,
  category: Category,
): Section[] {
  if (["Return tracking", "Order tracking"].includes(category)) {
    return [
      headerDefaultLogo,
      lineDefault,
      trackingInfoDefault,
      trackableSummaryDefault,
      lineDefault,
      footerDefault,
    ];
  }

  if (category === "Marketing") {
    return [headerDefaultLogo, lineDefault, footerDefault];
  }

  return [headerDefaultLogo, lineDefault, footerDefault];
}

export const getCityStateZip = (team: any): string => {
  if (!team.address?.city && !team.address?.state && !team.address?.zip) {
    return "Your city, state, zip code";
  }
  const stateZip = `${team.address?.state} ${team.address?.zip}`;
  if (!team.address?.city) {
    return stateZip;
  }

  return `${team.address?.city}, ${stateZip}`;
};

export const getDefaultTemplate = (params: {
  name: string;
  team: {
    _id: ObjectId | string;
    address?: {
      street1?: string | null;
      city?: string | null;
      state?: string | null;
      zip?: string | null;
      country_name?: string | null;
    } | null;
  };
  type: TemplateType;
  schemaType: SchemaType;
  category: Category;
  subject?: string;
  emailPreview?: string;
}): Omit<IEmailTemplate, "_id"> => {
  return {
    name: params.name,
    templateType: params.type,
    subject: params.subject || "",
    emailPreview: params.emailPreview || "",
    sections: getDefaultSections(params.schemaType, params.category),
    emailBackgroundColor: "#ffffff",
    contentBackgroundColor: "#f5f5f5",
    linkColor: "#0000ee",
    address: {
      businessAddress: "",
      legalAddress: params.team.address?.street1 || "Your legal address",
      cityStateZip: getCityStateZip(params.team),
      country: params.team.address?.country_name || "Your country",
    },
    team: params.team._id.toString(),
    schemaType: params.schemaType,
    category: params.category,
  };
};
